<template>
    <Transition name="fade" mode="out-in">
        <HomeMyWorkInsightCarousel
            v-if="showMyWorkInsightsBanner"
            :insights="homepageMyworkInsights"
            :perspective-enabled="perspectiveEnabled"
            :collaboration-profile-enabled="collaborationProfileEnabled"
            :work-iq-enabled="workIqEnabled"
            :calendar-integration-enabled="calendarIntegrationEnabled"
            :perspective-present="hasPerspectiveResult"
            :collaboration-profile-present="hasCollaborationProfile"
            :work-iq-present="hasWorkiqConnected"
            @retire="showMyWorkInsightsBanner = false" />
        <div v-else-if="shouldDisplay">
            <h3 class="scaffold-body text-valence-grey-850">How can I help you today?</h3>
            <hr class="border-valence-grey-250 mt-2 mb-2" />
            <div class="flex flex-col lg:flex-row gap-4">
                <Transition name="fade" mode="out-in">
                    <MyWorkActivationCard v-if="myworkCard" class="flex-1 min-w-60 lg:max-w-72" :card-state="myworkCard.state" :card-info="myworkCard.insight" />
                    <ProfileCompletionCard v-else-if="profileCompletionCard" class="flex-1 min-w-60 lg:max-w-72" :card-sections="profileCompletionCard.sections" />
                    <ManagerSelectorCard v-else-if="shouldShowManagerSelector" class="flex-1 min-w-60 lg:max-w-72" @success="handleManagerSelectionSuccess" />
                    <!-- Setup includes the assessment, so it replaces the Perspectives card to avoid
                         prompting for the same task twice.
                         -->
                    <GuidedSetupHomeCard v-else-if="shouldShowGuidedSetupCardFirst && guidedSetupCard" class="flex-1 min-w-60 lg:max-w-[310px]" :card="guidedSetupCard" />
                    <PerspectivesLinkCard
                        v-else-if="shouldShowPerspectivesIncompleteCard"
                        class="flex-1 min-w-60 lg:max-w-[310px]"
                        :perspective-report="perspectiveReport"
                        :is-fetching="isFetching"
                        @refresh="refresh" />
                    <CollaborationProfileLinkCard
                        v-else-if="shouldShowCollaborationProfileCard"
                        class="flex-1 min-w-60 lg:max-w-72"
                        :has-perspective-result="hasPerspectiveResult" />
                    <GuidedSetupHomeCard v-else-if="shouldShowGuidedSetupCardLater && guidedSetupCard" class="flex-1 min-w-60 lg:max-w-[310px]" :card="guidedSetupCard" />
                    <PerspectivesLinkCard
                        v-else-if="shouldShowPerspectivesDiveDeeperCard"
                        class="flex-1 min-w-60 lg:max-w-72"
                        :perspective-report="perspectiveReport"
                        :is-fetching="isFetching"
                        @refresh="refresh" />
                </Transition>
                <DiscoverUseCasesDisplay
                    v-if="shouldShowDiscoverEntrances"
                    class="flex-1"
                    :show-title="false"
                    :use-cases="useCases.cases"
                    :location="ChatUseCaseLocation.DISCOVER_HOMEPAGE"
                    @insight-hover="(...args) => $emit('insight-hover', ...args)"
                    @insight-hover-end="(...args) => $emit('insight-hover-end', ...args)"
                    @insight-clicked="(...args) => $emit('insight-clicked', ...args)" />
            </div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import type { PerspectiveReport, UserDetails } from "~vue/appTypes";
import CollaborationProfileLinkCard from "~vue/ChatHome/CollaborationProfileLinkCard.vue";
import GuidedSetupHomeCard from "~vue/ChatHome/GuidedSetupHomeCard.vue";
import HomeMyWorkInsightCarousel from "~vue/ChatHome/HomeMyWorkInsightCarousel.vue";
import ManagerSelectorCard from "~vue/ChatHome/ManagerSelectorCard.vue";
import MyWorkActivationCard from "~vue/ChatHome/MyWorkActivationCard.vue";
import PerspectivesLinkCard from "~vue/ChatHome/PerspectivesLinkCard.vue";
import ProfileCompletionCard from "~vue/ChatHome/ProfileCompletionCard.vue";
import DiscoverUseCasesDisplay from "~vue/ChatUseCaseEntrance/DiscoverUseCasesDisplay.vue";
import { ChatUseCaseLocation } from "~vue/ChatUseCaseEntrance/utils";
import { usePerspectiveReportImport } from "~vue/composables/usePerspectiveReportImport";
import type { SetupHomeCard } from "~vue/ProfileSetup/types";
import type { ChatEntranceUseCasesResponse, HomepageMyWorkInsight, MyWorkCardData, ProfileCompletionSummary } from "~vue/types/chat_home";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, type Ref, ref } from "vue";

const props = withDefaults(
    defineProps<{
        useCases?: ChatEntranceUseCasesResponse;
        perspectiveReportEnabled?: boolean;
        perspectiveReport?: PerspectiveReport | null;
        perspectiveAssessmentEnabled?: boolean;
        perspectiveCardDismissed?: boolean;
        myworkCard?: MyWorkCardData | null;
        profileCompletionCard?: ProfileCompletionSummary | null;
        /** `null` unless the coaching setup is offered to this user and something is still outstanding. */
        guidedSetupCard?: SetupHomeCard | null;
        collaborationProfileEnabled?: boolean;
        hasCollaborationProfile?: boolean;
        homepageMyworkInsightsEnabled?: boolean;
        homepageMyworkInsights?: HomepageMyWorkInsight[];
        workIqEnabled?: boolean;
        calendarIntegrationEnabled?: boolean;
        hasWorkiqConnected?: boolean;
    }>(),
    {
        useCases: () => ({ enabled: false, cases: [] }),
        perspectiveReportEnabled: false,
        perspectiveReport: null,
        perspectiveAssessmentEnabled: false,
        perspectiveCardDismissed: false,
        myworkCard: null,
        profileCompletionCard: null,
        guidedSetupCard: null,
        collaborationProfileEnabled: false,
        hasCollaborationProfile: false,
        homepageMyworkInsightsEnabled: false,
        homepageMyworkInsights: () => [],
        workIqEnabled: false,
        calendarIntegrationEnabled: true,
        hasWorkiqConnected: false,
    },
);

const showMyWorkInsightsBanner = ref(props.homepageMyworkInsightsEnabled);

const emit = defineEmits(["insight-clicked", "insight-hover", "insight-hover-end", "manager-selection-success"]);

const userDetails = inject<Ref<UserDetails>>("user_details");

const page = usePage();

// The Team Tools import polling lives here (not in a card) so it runs regardless of which
// home-page card wins priority. `perspectiveReport` reflects the latest imported report.
const { perspectiveReport, refresh, isFetching } = usePerspectiveReportImport({
    reportEnabled: () => props.perspectiveReportEnabled,
    assessmentEnabled: () => props.perspectiveAssessmentEnabled,
    initialReport: props.perspectiveReport,
    onReportFound: () => router.reload({ only: ["perspective_archetype"] }),
});

const perspectiveEnabled = computed(() => props.perspectiveReportEnabled || props.perspectiveAssessmentEnabled);

const hasPerspectiveResult = computed(() => !!page.props.perspective_assessment_completed || !!page.props.perspective_archetype || !!perspectiveReport.value);

const shouldShowManagerSelector = computed(() => userDetails?.value.is_manager === null);

const perspectivesIncomplete = computed(() => perspectiveEnabled.value && !hasPerspectiveResult.value);
const perspectivesComplete = computed(() => perspectiveEnabled.value && hasPerspectiveResult.value);

const shouldShowPerspectivesIncompleteCard = computed(() => !props.perspectiveCardDismissed && perspectivesIncomplete.value);

const shouldShowCollaborationProfileCard = computed(() => props.collaborationProfileEnabled && !props.hasCollaborationProfile);

const shouldShowPerspectivesDiveDeeperCard = computed(() => !props.perspectiveCardDismissed && perspectivesComplete.value);

/*
 * The setup card takes the Perspectives card's place in the order, but not its dismissal: it carries four
 * asks, and declining the assessment prompt is not a decision about the calendar or the profile. It
 * retires on its own -- the server omits it once every ask is satisfied.
 */
const shouldShowGuidedSetupCardFirst = computed(() => props.guidedSetupCard !== null && perspectivesIncomplete.value);
const shouldShowGuidedSetupCardLater = computed(() => props.guidedSetupCard !== null && perspectivesComplete.value);

const shouldShowDiscoverEntrances = computed(() => props.useCases.enabled && props.useCases.cases.length > 0);

const hasCompanionCard = computed(
    () =>
        !!props.myworkCard ||
        !!props.profileCompletionCard ||
        shouldShowManagerSelector.value ||
        shouldShowGuidedSetupCardFirst.value ||
        shouldShowPerspectivesIncompleteCard.value ||
        shouldShowCollaborationProfileCard.value ||
        shouldShowGuidedSetupCardLater.value ||
        shouldShowPerspectivesDiveDeeperCard.value,
);

const shouldDisplay = computed(() => hasCompanionCard.value || shouldShowDiscoverEntrances.value);

const handleManagerSelectionSuccess = (value: string) => {
    logUserInteraction(AmplitudeEventNames.MANAGER_SELECTOR_CONFIRMED, { selection: value });
    if (userDetails) {
        userDetails.value.is_manager = value === "yes";
    }
    emit("manager-selection-success", value);
};
</script>

<style scoped>
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}
</style>
